import {ReactNode, useCallback, useEffect, useRef} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {getDataset} from '../../../../../../common/shared/utils/getDataset';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {useWindowEventBlockerRef} from '../../../../../../common/useWindowEventBlockerRef.hook';
import {useViewerEndQuery} from '../../../../../../wrapper/viewerWrapper';
import {
  CONTENTS_MOBILE_MAX_WIDTH,
  CONTENTS_PC_MAX_WIDTH,
  SCROLL_TOP_IMAGE_VIEWER_EVENT_NAME,
} from '../constant/viewerConstant';
import {useViewerMenuHandler} from '../hook/useViewerMenuHandler';
import {setCurrentPage, setIsViewerEnd} from '../state/imageViewerSlice';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {isElementInView} from '../util/isElementInView';
import {ImageViewerViewerEndWrapper} from './ImageViewerViewerEndWrapper';
import {ScrollImageViewerImage} from './ScrollImageViewerImage';
import {ScrollViewerNonStopWatchingBottomContainer} from './ScrollViewerNonStopWatchingBottomContainer';
import {ViewerProgressContainer} from './ViewerProgressContainer';

export function ScrollImageViewerContainer({
  seriesId,
  productId,
  isPreview = false,
  children,
}: {
  seriesId: number;
  productId: number;
  isPreview?: boolean;
  children?: ReactNode;
}) {
  const {isMobile} = requestContext.requestInfo.display;
  const {shouldUseNonstopWatching} = useViewerMenuHandler({isMobile});
  const wrapperRef = useRef<HTMLDivElement>(null);

  const dispatch = useTypedDispatch();

  const viewerEndResult = useViewerEndQuery({seriesId, productId});
  const viewerEndData = viewerEndResult.data;

  const {downloadData, currentPage, totalPage, readType, isHandlingSeekBar} =
    useTypedSelector(state => ({
      downloadData: state.imageViewer.viewData[productId]?.downloadData,
      currentPage: state.imageViewer.viewData[productId]?.currentPage || 1,
      totalPage: state.imageViewer.viewData[productId]?.totalPage || 1,
      readType: state.imageViewer.settings.readType,
      isHandlingSeekBar:
        state.imageViewer.viewData[productId]?.isHandlingSeekBar,
    }));

  // 스크롤중인지 아닌지를 판단하기 위해 사용됩니다.
  const scrollingTimer = useRef<ReturnType<typeof setTimeout>>();
  const isScrolling = useRef<boolean>(false);

  // scrollInToView나 scrollTo 실행 시 뒤에 실행되는 onScroll을 막기위해 사용됩니다.
  const windowClickEventBlockerRef = useWindowEventBlockerRef('scroll');

  // 현재 페이지 정보에 따라 이동하는 함수
  const scrollToCurrentPage = useCallback(() => {
    const targetPage = shouldUseNonstopWatching
      ? currentPage - 2 > 0
        ? currentPage - 2
        : 0
      : currentPage - 1;
    const currentPageElement = wrapperRef.current?.querySelector(
      `[data-index="${targetPage}"]`
    );
    if (!currentPageElement) return;

    currentPageElement.scrollIntoView(true);

    const _isViewerEnd = currentPage >= totalPage;
    dispatch(setIsViewerEnd({productId, isViewerEnd: _isViewerEnd}));
  }, [currentPage, dispatch, productId, shouldUseNonstopWatching, totalPage]);

  // currentPage가 변경되거나 readType이 변경되었을 때 실행됩니다.
  useEffect(() => {
    if (
      !downloadData ||
      !wrapperRef ||
      isPreview ||
      (!shouldUseNonstopWatching && !viewerEndData) ||
      isScrolling.current
    ) {
      return;
    }
    // 시크바를 통한 동작일 때나 열람 방식이 변경했을 때 해당 위치로 이동시켜줌
    scrollToCurrentPage();
    windowClickEventBlockerRef.current = true;
  }, [
    currentPage,
    downloadData,
    isPreview,
    readType,
    scrollToCurrentPage,
    shouldUseNonstopWatching,
    viewerEndData,
    windowClickEventBlockerRef,
  ]);

  /**
   * onScroll이 scrollToTop 이후 발생하는 것을 한번 막기 위해 사용됩니다. block이 먼저 등록 되어야하기 때문에 순서가 중요합니다.
   * 상단으로 버튼으로 스크롤이 변경되었을 때 실행 됨. observer로 감지 안되기 때문에 currentPage와 currentProgress를 초기화해 줌
   */
  useEffect(() => {
    function scrollToTop() {
      window.scrollTo(0, 0);
      dispatch(setCurrentPage({productId, page: 1}));
      dispatch(setIsViewerEnd({productId, isViewerEnd: false}));
      windowClickEventBlockerRef.current = true;
    }
    window.addEventListener(SCROLL_TOP_IMAGE_VIEWER_EVENT_NAME, scrollToTop);
    return () =>
      window.removeEventListener(
        SCROLL_TOP_IMAGE_VIEWER_EVENT_NAME,
        scrollToTop
      );
  }, [dispatch, productId, windowClickEventBlockerRef]);

  /**
   * 실제로 화면에 보이고 있는 요소를 찾아 현재 페이지를 업데이트 해줍니다.
   * 화면에는 여러개의 이미지가 존재할 수 있는데 가장 마지막에 있는 이미지를 현재 페이지로 계산합니다.
   * 마지막 요소를 현재 보고있는 페이지로 하는 이유는 뷰어앤드를 보고있는지 여부를 체크할 수 있고 스크롤의 흐름과도 맞기 때문입니다.
   */
  const updateCurrentPageIsVisible = useCallback(() => {
    if (
      !downloadData ||
      (!shouldUseNonstopWatching && !isPreview && !viewerEndData)
    ) {
      return;
    }
    // data-visible이 true인 요소들을 찾아 마지막에 있는 것이 현재 보고 있는 페이지 입니다.
    const _elements = wrapperRef.current?.querySelectorAll('[data-index]') as
      | NodeListOf<HTMLElement>
      | undefined;

    if (!_elements) {
      return;
    }

    const elements = Array.from(_elements).filter(element =>
      isElementInView(element)
    );

    if (!elements || elements.length === 0) {
      return;
    }

    const indexString = getDataset(elements[elements.length - 1], 'index');
    const index = Number(indexString);
    const firstIndex = Number(getDataset(elements[0], 'index'));
    if (Number.isNaN(firstIndex) || Number.isNaN(index)) {
      return;
    }

    if (!isPreview && firstIndex === 0) {
      // 스크롤 시 지금 보이고 요소 인덱스에 맞춰 currentPage와 viewerEnd 체크를 해줍니다.
      dispatch(setCurrentPage({productId, page: 1}));
      dispatch(setIsViewerEnd({productId, isViewerEnd: false}));
      return;
    }

    // 스크롤 시 지금 보이고 요소 인덱스에 맞춰 currentPage와 viewerEnd 체크를 해줍니다.
    dispatch(setCurrentPage({productId, page: index + 1}));
    const _isViewerEnd = index > totalPage - 1;
    dispatch(setIsViewerEnd({productId, isViewerEnd: _isViewerEnd}));
  }, [
    dispatch,
    downloadData,
    isPreview,
    productId,
    shouldUseNonstopWatching,
    totalPage,
    viewerEndData,
  ]);

  // 스크롤이 발생했을때 실행됩니다.
  const onScroll = useCallback(() => {
    if (!downloadData) {
      return;
    }
    // 스크롤이 일어났을 때 현재 화면에 보이는 이미지 기준으로 페이지를 업데이트 해줍니다.
    updateCurrentPageIsVisible();

    if (!isHandlingSeekBar && !isPreview) {
      // 스크롤 중에 뷰어 메뉴가 열려있다면 닫습니다.
      dispatch(setIsViewerMenuExposed({isExposed: false}));
    }
  }, [
    dispatch,
    downloadData,
    isHandlingSeekBar,
    isPreview,
    updateCurrentPageIsVisible,
  ]);

  useEffect(() => {
    const scrolling = function () {
      if (!isScrolling.current) {
        //scroll start
        isScrolling.current = true;
      }
      //scrolling
      onScroll();
      scrollingTimer.current && clearTimeout(scrollingTimer.current);
      scrollingTimer.current = setTimeout(() => {
        //scroll end
        isScrolling.current = false;
      }, 100);
    };
    window.addEventListener('scroll', scrolling);
    return () => {
      window.removeEventListener('scroll', scrolling);
    };
  }, [onScroll]);

  if (!downloadData) {
    return null;
  }
  return (
    <div ref={wrapperRef} className="w-full">
      <div className="w-full">
        <div
          className="mx-auto"
          style={{
            maxWidth: isMobile
              ? CONTENTS_MOBILE_MAX_WIDTH
              : CONTENTS_PC_MAX_WIDTH,
          }}
        >
          {downloadData.files.map((file, index) => {
            const url = file.secureUrl;
            const width = file.width ?? 0;
            const height = file.height ?? 0;
            const gap = downloadData.gapBetweenImages;
            const aspectRatio = width !== 0 ? height / width : 0;

            return (
              <ScrollImageViewerImage
                key={url}
                url={url}
                gap={gap}
                index={index}
                aspectRatio={aspectRatio}
              />
            );
          })}
        </div>
      </div>
      {/* 첫 화 보기 및 정주행 모드에서는 뷰어엔드를 노출하지 않습니다. */}
      {!shouldUseNonstopWatching && !isPreview && (
        <div
          className={twMerge(
            'mx-auto',
            isMobile ? 'bg-theme-bg-surface' : 'py-116pxr'
          )}
          style={{
            maxWidth: isMobile
              ? CONTENTS_MOBILE_MAX_WIDTH
              : CONTENTS_PC_MAX_WIDTH,
          }}
        >
          <ImageViewerViewerEndWrapper index={downloadData.files.length}>
            {children}
          </ImageViewerViewerEndWrapper>
        </div>
      )}
      {shouldUseNonstopWatching && !isPreview && (
        <ScrollViewerNonStopWatchingBottomContainer />
      )}
      {!isPreview && (
        <ViewerProgressContainer seriesId={seriesId} productId={productId} />
      )}
    </div>
  );
}
